import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { IconCrown, IconTarget, IconCode, IconCpu, IconTrendingUp } from "@tabler/icons-react";
import { ArrowRight } from "lucide-react";
import { AmbientGlow } from "@/components/AmbientGlow";
import { useCalendly } from "@/components/CalendlyContext";

const EASE = [0.16, 1, 0.3, 1];

function RevealLine({ children, delay, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

const marqueeItems = [
  { label: "STRATEGY", Icon: IconCrown },
  { label: "CREATIVE", Icon: IconTarget },
  { label: "TECHNOLOGY", Icon: IconCode },
  { label: "AI AUTOMATION", Icon: IconCpu },
  { label: "GROWTH", Icon: IconTrendingUp },
];

export function Hero() {
  const { openCalendly } = useCalendly();

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[85vh] w-full flex flex-col overflow-hidden"
    >
      <AmbientGlow />

      <div className="relative z-10 flex-1 flex items-center justify-center pt-20 pb-6">
        <div className="container-custom flex flex-col items-center text-center">
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight text-white">
            <RevealLine delay={0.1}>WE BUILD</RevealLine>
            <RevealLine delay={0.22}>BUSINESSES</RevealLine>
            <RevealLine delay={0.34}>
              TO <span className="grow-gradient-text">GROW.</span>
            </RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            data-testid="hero-subline"
            className="mt-6 text-xs sm:text-sm tracking-[0.3em] uppercase text-zinc-500 font-medium"
          >
            Strategy. Marketing. AI. Growth.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-8"
          >
            <button
              data-testid="hero-book-call-btn"
              onClick={openCalendly}
              className="group inline-flex items-center gap-3 bg-white text-black font-bold text-xs tracking-widest uppercase px-9 py-5 hover:bg-violet-500 hover:text-white transition-colors duration-300"
            >
              Book A Strategy Call
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>
        </div>
      </div>

      <Link
        to="/services"
        data-testid="hero-marquee"
        aria-label="View all services"
        className="relative z-10 block border-t border-white/10 bg-black/40 py-4 md:py-5 overflow-hidden"
      >
        <Marquee speed={26} gradient={false} autoFill>
          {marqueeItems.map((item, i) => (
            <span key={item.label + i} className="flex items-center">
              <item.Icon size={16} className="text-violet-400 mr-2 shrink-0 md:w-5 md:h-5" stroke={1.5} />
              <span className="font-display font-bold text-sm md:text-base tracking-wide text-white uppercase whitespace-nowrap">
                {item.label}
              </span>
              <span className="text-zinc-600 mx-5 md:mx-8">×</span>
            </span>
          ))}
        </Marquee>
      </Link>
    </section>
  );
}
